import { ImagingTemplate, type ImagingTemplateDetail } from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import { buildSingleItemPdfEntry } from './pdfOverride';

type ImagingRecordsKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_RECORDS; type: DocumentType.IMAGING }
>;

interface ImagingDetailRaw {
  id: string;
  curated: {
    id: string;
    title: string | null;
    date: string | null;
    alternateBodyPartName?: string;
    comments?: string;
    createdAt: string | Date;
    imagingType: { name: string; displayName: string } | null;
    bodyPart: { name: string; displayName: string } | null;
    images: Array<{ id: string; signedUrl?: string; createdAt: string | Date }>;
  };
}

export interface ImagingRecordsQueryData {
  details: ImagingDetailRaw[];
}

export const adaptImagingRecords = (
  data: ImagingRecordsQueryData,
  key: ImagingRecordsKey,
): DocumentEntry | undefined => {
  const details = data?.details ?? [];
  if (details.length === 0) return undefined;

  const mapped: ImagingTemplateDetail[] = details.map((detail) => ({
    id: detail.curated.id,
    title: detail.curated.title,
    date: detail.curated.date,
    imagingType: detail.curated.imagingType?.displayName,
    bodyPart: detail.curated.bodyPart?.displayName,
    alternateBodyPartName: detail.curated.alternateBodyPartName,
    comments: detail.curated.comments,
    images: detail.curated.images,
  }));

  const pdfEntry = buildSingleItemPdfEntry(
    details,
    (detail) =>
      detail.curated.images.map((image) => ({
        signedUrl: image.signedUrl,
        title: detail.curated.title ?? key.name,
        uploadedAt: image.createdAt ?? detail.curated.createdAt,
      })),
    { title: key.name, uploadedAt: details[0]?.curated.createdAt },
  );
  if (pdfEntry) return pdfEntry;

  return {
    kind: 'template',
    title: key.name,
    render: (ctx) => (
      <ImagingTemplate
        details={mapped}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
      />
    ),
  };
};
